import { ptBR } from '@/i18n/pt-BR';
import {
  formatarCentavos,
  formatarDataVigencia,
  type PrecificacaoHistorico,
  type PrecificacaoVersao,
} from '@/lib/precificacao';
import {
  Blueprint,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface HistoricoViewProps {
  historico: PrecificacaoHistorico | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

export function HistoricoView({ historico, loading, error, onRetry }: HistoricoViewProps) {
  if (loading) {
    return (
      <p data-testid="historico-loading" className="text-body-md text-fg-muted">
        {ptBR.precificacao.historicoLoading}
      </p>
    );
  }
  if (error !== null) {
    return (
      <div data-testid="historico-error" className="flex flex-col items-start gap-2">
        <p className="text-body-md text-fg">{error}</p>
        <Button variant="secondary" size="sm" type="button" onClick={onRetry}>
          {ptBR.precificacao.listErrorRetry}
        </Button>
      </div>
    );
  }
  if (historico === null || historico.versoes.length === 0) {
    return (
      <Blueprint data-testid="historico-empty" className="p-sc-4">
        <p className="text-body-md font-medium text-fg">{ptBR.precificacao.historicoEmptyTitle}</p>
        <p className="mt-1 text-body-sm text-fg-muted">{ptBR.precificacao.historicoEmptyHint}</p>
      </Blueprint>
    );
  }
  return (
    <ul data-testid="historico-list" className="flex flex-col gap-3">
      {historico.versoes.map((versao) => (
        <VersaoCard key={versao.tabela.id} versao={versao} />
      ))}
    </ul>
  );
}

function VersaoCard({ versao }: { versao: PrecificacaoVersao }) {
  const { tabela, faixas, vigente } = versao;
  const periodo = `${formatarDataVigencia(tabela.vigencia_inicio)} ${ptBR.precificacao.vigenciaAte} ${formatarDataVigencia(tabela.vigencia_fim)}`;
  return (
    <li
      data-testid={`versao-${tabela.id}`}
      className="overflow-hidden border border-border bg-surface"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="text-body-sm text-fg">
          {ptBR.precificacao.vigenciaLabel}: {periodo}
        </span>
        {vigente ? (
          <span
            data-testid={`vigente-${tabela.id}`}
            className="bg-accent-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-accent-700"
          >
            {ptBR.precificacao.vigenteBadge}
          </span>
        ) : null}
      </header>
      <div className="overflow-x-auto [contain:layout]">
        <Table>
          <caption className="sr-only">{periodo}</caption>
          <TableHeader>
            <TableRow className="text-fg-muted">
              <TableHead scope="col" className="font-medium">
                {ptBR.precificacao.colFaixa}
              </TableHead>
              <TableHead scope="col" className="font-medium">
                {ptBR.precificacao.colVolume}
              </TableHead>
              <TableHead scope="col" className="font-medium">
                {ptBR.precificacao.colExame}
              </TableHead>
              <TableHead scope="col" className="font-medium">
                {ptBR.precificacao.colCertificacao}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {faixas.map((faixa) => (
              <TableRow key={faixa.id} className="text-fg">
                <TableCell>{faixa.ordem}</TableCell>
                <TableCell>
                  {faixa.volume_fim === null
                    ? `${faixa.volume_inicio} ${ptBR.precificacao.volumeAberto}`
                    : `${faixa.volume_inicio}–${faixa.volume_fim}`}
                </TableCell>
                <TableCell>{formatarCentavos(faixa.valor_exame_centavos)}</TableCell>
                <TableCell>
                  {formatarCentavos(faixa.valor_certificacao_agregado_centavos)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </li>
  );
}
